import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Plus, MapPin, Calendar, TrendingUp, Users, Loader, X, ExternalLink, Home, Clock, CheckCircle, Trash2, Eye, ShoppingCart, CreditCard, DollarSign } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';
import { useNavigate } from 'react-router-dom';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Campaign {
  id: string;
  name: string;
  campaign_type: 'buyers_agent_outreach' | 'customer_outreach' | 'sellers_agent_outreach';
  campaign_subtype: 'closing_anniversary_agent' | 'recently_closed_agent' | 'recently_closed_customer' | 'under_contract_sellers_agent' | 'sold_sellers_agent';
  total_properties_found: number;
  created_at: string;
  updated_at: string;
  last_scraped_at?: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  screenshot_thumbnail_url?: string;
  request_url: string;
  source_platform: 'homes.com' | 'zillow';
}

interface CampaignAnalytics {
  total_properties: number;
  properties_with_artwork: number;
  total_property_opens: number;
  unique_property_opens: number;
  total_configurator_views: number;
  unique_configurator_views: number;
  total_checkouts: number;
  unique_checkout_users: number;
  total_payment_pages: number;
  unique_payment_users: number;
  total_purchases: number;
  unique_buyers: number;
  total_revenue: number;
  average_order_value: number;
  open_to_checkout_rate: number;
  checkout_to_payment_rate: number;
  payment_to_purchase_rate: number;
  overall_conversion_rate: number;
  outreach_sent: number;
  outreach_opened: number;
  outreach_clicked: number;
  // Funnel tracking metrics
  not_ready: number;
  ready_to_send: number;
  email_sent: number;
  landing_page_accessed: number;
  configurator_accessed: number;
  order_summary_accessed: number;
  checkout_accessed: number;
  stripe_accessed: number;
  purchased: number;
}

interface ScrapedProperty {
  id: string;
  campaign_id: string;
  address: string;
  city: string;
  state: string;
  price: string;
  soldDate: string;
  details?: any;
  // Artwork URLs
  watercolor_image_url?: string;
  oil_image_url?: string;
  pencil_image_url?: string;
  charcoal_image_url?: string;
  // Tracking fields
  buyers_agent_email?: string;
  outreach_status?: string;
  outreach_sent_at?: string;
  // Funnel tracking (with counters for multiple opens)
  landing_page_accessed_count?: number;
  landing_page_accessed_at?: string;
  configurator_opened_count?: number;
  configurator_opened_at?: string;
  order_summary_accessed_count?: number;
  order_summary_accessed_at?: string;
  checkout_initiated_count?: number;
  checkout_initiated_at?: string;
  payment_page_accessed_count?: number;
  payment_page_accessed_at?: string;
  purchase_completed_at?: string;
  purchase_amount?: number;
  raw_data?: any;
}

const CampaignDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [campaignStats, setCampaignStats] = useState<{ [key: string]: CampaignAnalytics }>({});

  // Database testing state
  const [testingDatabase, setTestingDatabase] = useState(false);
  const [databaseTestResult, setDatabaseTestResult] = useState<string | null>(null);

  // New campaign popup state
  const [showNewCampaignPopup, setShowNewCampaignPopup] = useState(false);
  const [campaignType, setCampaignType] = useState<'homes' | 'zillow' | null>(null); // New state for campaign type
  const [newCampaignData, setNewCampaignData] = useState({
    name: '',
    homesUrl: '',
    zillowArea: '',

    redfinArea: '',
    redfinAreaType: 'zipcode' as 'zipcode' | 'county',
    redfinSoldArea: '',  // New field for sold listings
    redfinSoldAreaType: 'zipcode' as 'zipcode' | 'county',  // New field for sold listings
    targetProperties: 50,
    targetPages: 3, // New field for page-based scraping
    priceMin: 100000, // More inclusive - $100K
    priceMax: 10000000, // More inclusive - $10M
    requireAgentEmail: false, // New option for email filtering
    notes: '',
    campaignId: ''
  });

  // Scraping state
  const [scrapingStep, setScrapingStep] = useState<'url' | 'scraping' | 'choose_pages' | 'final_scraping' | 'page_by_page' | 'complete'>('url');
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  const [scrapedData, setScrapedData] = useState<any>(null);
  const [selectedScrapeType, setSelectedScrapeType] = useState<'recently_sold' | 'sold_year_ago' | null>(null);
  const [finalProperties, setFinalProperties] = useState<ScrapedProperty[]>([]);
  const [scrapingProgress, setScrapingProgress] = useState({ current: 0, total: 0 });

  // Page-by-page scraping state
  const [pageByPageScraping, setPageByPageScraping] = useState(false);
  const [pageByPageCancelled, setPageByPageCancelled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPropertiesFound, setTotalPropertiesFound] = useState(0);
  const [totalPropertiesWithEmails, setTotalPropertiesWithEmails] = useState(0);
  const [pageByPageProgress, setPageByPageProgress] = useState({
    currentPage: 0,
    totalPages: 0,
    propertiesFound: 0,
    propertiesWithEmails: 0,
    targetReached: false,
    lastPageResults: null as any,
    // County-specific progress
    isCountyMode: false,
    zipCodes: [] as string[],
    currentZipIndex: 0,
    currentZipCode: '',
    zipCodeProgress: [] as any[]
  });

  // Delete confirmation state
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
    loadCampaigns();
  }, [navigate]);

  // Add funnel tracking logic (same as CampaignPropertiesTable)
  const getTrackingFunnelStatus = (property: ScrapedProperty) => {
    // Check for purchase first (highest funnel level)
    if (property.purchase_completed_at) {
      return 'purchased';
    }
    
    // Check for Stripe/payment access
    if (property.payment_page_accessed_count && property.payment_page_accessed_count > 0) {
      return 'stripe_accessed';
    }
    
    // Check for order summary access
    if (property.order_summary_accessed_count && property.order_summary_accessed_count > 0) {
      return 'order_summary_accessed';
    }
    
    // Check for checkout access
    if (property.checkout_initiated_count && property.checkout_initiated_count > 0) {
      return 'checkout_accessed';
    }
    
    // Check for property opened (configurator access)
    if (property.configurator_opened_count && property.configurator_opened_count > 0) {
      return 'configurator_accessed';
    }
    
    // Check for landing page accessed
    if (property.landing_page_accessed_count && property.landing_page_accessed_count > 0) {
      return 'landing_page_accessed';
    }
    
    // Check for email sent
    if (property.outreach_status === 'sent' || property.outreach_status === 'delivered' || property.outreach_status === 'opened' || property.outreach_status === 'clicked') {
      return 'email_sent';
    }
    
    // Check if ready to send (has artwork and agent email)
    const hasArtwork = property.watercolor_image_url ||
                      property.oil_image_url ||
                      property.pencil_image_url ||
                      property.charcoal_image_url ||
                      property.raw_data?.artworkGeneration?.artworkUrl;
    
    const hasAgentEmail = property.buyers_agent_email && property.buyers_agent_email.trim() !== '';
    
    if (hasArtwork && hasAgentEmail) {
      return 'ready_to_send';
    }
    
    // Default fallback
    return 'not_ready';
  };

  // Calculate funnel metrics for campaign properties
  const calculateFunnelMetrics = (properties: ScrapedProperty[]) => {
    const funnel = {
      not_ready: 0,
      ready_to_send: 0,
      email_sent: 0,
      landing_page_accessed: 0,
      configurator_accessed: 0,
      order_summary_accessed: 0,
      checkout_accessed: 0,
      stripe_accessed: 0,
      purchased: 0
    };

    properties.forEach(property => {
      const status = getTrackingFunnelStatus(property);
      funnel[status as keyof typeof funnel]++;
    });

    return funnel;
  };

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading campaigns:', error);
        return;
      }

      setCampaigns(data || []);

      // Load stats for each campaign
      for (const campaign of data || []) {
        await loadCampaignStats(campaign.id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignStats = async (campaignId: string) => {
    try {
      // Load traditional analytics from RPC function
      const { data: analyticsData, error: analyticsError } = await supabase.rpc('get_campaign_analytics_v3', {
        campaign_uuid: campaignId,
        days_back: 30
      });

      if (analyticsError) {
        console.error('Error loading campaign analytics:', analyticsError);
        return;
      }

      // Load campaign properties for funnel metrics
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('scraped_properties')
        .select(`
          id,
          campaign_id,
          watercolor_image_url,
          oil_image_url,
          pencil_image_url,
          charcoal_image_url,
          buyers_agent_email,
          outreach_status,
          outreach_sent_at,
          landing_page_accessed_count,
          landing_page_accessed_at,
          configurator_opened_count,
          configurator_opened_at,
          order_summary_accessed_count,
          order_summary_accessed_at,
          checkout_initiated_count,
          checkout_initiated_at,
          payment_page_accessed_count,
          payment_page_accessed_at,
          purchase_completed_at,
          purchase_amount,
          raw_data
        `)
        .eq('campaign_id', campaignId);

      if (propertiesError) {
        console.error('Error loading campaign properties:', propertiesError);
        return;
      }

      // Calculate funnel metrics
      const funnelMetrics = calculateFunnelMetrics((propertiesData || []) as any);

      if (analyticsData && analyticsData.length > 0) {
        // Combine traditional analytics with funnel metrics
        setCampaignStats(prev => ({
          ...prev,
          [campaignId]: {
            ...analyticsData[0],
            ...funnelMetrics
          }
        }));
      } else {
        // If no analytics data, just use funnel metrics with defaults
        setCampaignStats(prev => ({
          ...prev,
          [campaignId]: {
            total_properties: propertiesData?.length || 0,
            properties_with_artwork: 0,
            total_property_opens: 0,
            unique_property_opens: 0,
            total_configurator_views: 0,
            unique_configurator_views: 0,
            total_checkouts: 0,
            unique_checkout_users: 0,
            total_payment_pages: 0,
            unique_payment_users: 0,
            total_purchases: 0,
            unique_buyers: 0,
            total_revenue: 0,
            average_order_value: 0,
            open_to_checkout_rate: 0,
            checkout_to_payment_rate: 0,
            payment_to_purchase_rate: 0,
            overall_conversion_rate: 0,
            outreach_sent: 0,
            outreach_opened: 0,
            outreach_clicked: 0,
            ...funnelMetrics
          }
        }));
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const testDatabase = async () => {
    setTestingDatabase(true);
    setDatabaseTestResult(null);

    try {
      // Test basic connection to campaigns table
      const { data, error } = await supabase
        .from('campaigns')
        .select('count', { count: 'exact', head: true });

      if (error) {
        if (error.message?.includes('relation "campaigns" does not exist')) {
          setDatabaseTestResult('❌ The campaigns table does not exist. Click "Fix Database" to resolve this.');
        } else if (error.code === 'PGRST116') {
          setDatabaseTestResult('❌ Database table not found (404). The database may need to be set up.');
        } else {
          setDatabaseTestResult(`❌ Database error: ${error.message}`);
        }
      } else {
        setDatabaseTestResult('✅ Database is working properly! Campaigns table is accessible.');
      }
    } catch (error) {
      setDatabaseTestResult(`❌ Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTestingDatabase(false);
    }
  };

  const handleNewCampaign = () => {
    setShowNewCampaignPopup(true);
    setScrapingStep('url');
    setNewCampaignData({ name: '', homesUrl: '', zillowArea: '', redfinArea: '', redfinAreaType: 'zipcode', redfinSoldArea: '', redfinSoldAreaType: 'zipcode', targetProperties: 50, targetPages: 3, priceMin: 100000, priceMax: 10000000, requireAgentEmail: false, notes: '', campaignId: '' });
    setScrapedData(null);
    setSelectedScrapeType(null);
    setScrapingError(null);
  };

  const handleClosePopup = () => {
    setShowNewCampaignPopup(false);
    setScrapingStep('url');
    setScrapingLoading(false);
    setScrapingError(null);
    setScrapedData(null);
    setSelectedScrapeType(null);
    setFinalProperties([]);
    setNewCampaignData({ name: '', homesUrl: '', zillowArea: '', redfinArea: '', redfinAreaType: 'zipcode', redfinSoldArea: '', redfinSoldAreaType: 'zipcode', targetProperties: 50, targetPages: 3, priceMin: 100000, priceMax: 10000000, requireAgentEmail: false, notes: '', campaignId: '' });
    setCampaignType(null);
    
    // Reset page-by-page state
    setPageByPageScraping(false);
    setPageByPageCancelled(false);
    setCurrentPage(1);
    setTotalPropertiesFound(0);
    setTotalPropertiesWithEmails(0);
    setPageByPageProgress({
      currentPage: 0,
      totalPages: 0,
      propertiesFound: 0,
      propertiesWithEmails: 0,
      targetReached: false,
      lastPageResults: null,
      isCountyMode: false,
      zipCodes: [],
      currentZipIndex: 0,
      currentZipCode: '',
      zipCodeProgress: []
    });
  };

  const handleInitialScrape = async () => {
    if (!newCampaignData.homesUrl.trim() && !newCampaignData.zillowArea.trim() && !newCampaignData.redfinArea.trim() && !newCampaignData.redfinSoldArea.trim()) {
      setScrapingError('Please enter either a homes.com URL, Zillow area, Redfin area for pending campaigns, or Redfin area for sold campaigns.');
      return;
    }

    if (newCampaignData.homesUrl.trim() && !newCampaignData.homesUrl.includes('homes.com')) {
      setScrapingError('Please enter a valid homes.com URL');
      return;
    }

    if (!newCampaignData.name.trim()) {
      setScrapingError('Please enter a campaign name');
      return;
    }

    setScrapingLoading(true);
    setScrapingError(null);
    setScrapingStep('scraping');

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Determine campaign type based on input
      const isZillowCampaign = newCampaignData.zillowArea.trim() !== '';
      const isHomesCampaign = newCampaignData.homesUrl.trim() !== '';
      const isRedfinCampaign = newCampaignData.redfinArea.trim() !== '';
      const isRedfinSoldCampaign = newCampaignData.redfinSoldArea.trim() !== '';

      if (isZillowCampaign) {
        console.log('Step 1: Creating campaign record first...');
        
        // Create campaign first to get a real UUID
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .insert({
            name: newCampaignData.name,
            campaign_type: 'sellers_agent_outreach',
            campaign_subtype: 'under_contract_sellers_agent',
            request_url: `${newCampaignData.zillowArea} ($${(newCampaignData.priceMin/1000000).toFixed(1)}M-$${(newCampaignData.priceMax/1000000).toFixed(1)}M)`,
            source_platform: 'zillow',
            status: 'active',
            total_properties_found: 0, // Will be updated after scraping
            created_by_admin_id: 'admin_user'
          })
          .select()
          .single();

        if (campaignError || !campaignData) {
          let errorMessage = 'Failed to create campaign';
          if (campaignError?.message?.includes('duplicate key value')) {
            errorMessage = 'Campaign name already exists. Please choose a different name.';
          } else if (campaignError) {
            errorMessage = `Database Error: ${campaignError.message || 'Unknown error occurred'}`;
          }
          throw new Error(errorMessage);
        }

        console.log('Campaign created with UUID:', campaignData.id);
        
        // Store the campaign ID for later use
        setNewCampaignData(prev => ({ ...prev, campaignId: campaignData.id }));

        console.log('Step 2: Getting under contract properties from Zillow with full details...');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-zillow-bulk-with-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            keyword: newCampaignData.zillowArea,
            campaignId: campaignData.id, // Use the real UUID
            priceMin: newCampaignData.priceMin || 1000000,
            priceMax: newCampaignData.priceMax || 5000000,
            targetProperties: newCampaignData.targetProperties,
            propertyStatus: 'pendingAndUnderContract'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.details || errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.details || data.message || 'Failed to fetch Zillow properties with details');
        }

        setScrapedData(data.results);
        
        // Step 3: Update campaign with final property count
        console.log('Step 3: Updating campaign with final property count...');
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: data.results.successful || 0 })
          .eq('id', campaignData.id);

        if (updateError) {
          console.error('Error updating campaign property count:', updateError);
          // Don't fail the whole process for this
        }
        
        // Refresh campaigns list to show the new campaign
        await loadCampaigns();
        
        setScrapingStep('complete'); // Skip choose_pages for under contract
        
      } else if (isRedfinCampaign) {
        console.log('Step 1: Creating Redfin campaign record first...');
        
        // Create campaign first to get a real UUID
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .insert({
            name: newCampaignData.name,
            campaign_type: 'sellers_agent_outreach',
            campaign_subtype: 'under_contract_sellers_agent',
            request_url: `${newCampaignData.redfinArea} ($${(newCampaignData.priceMin/1000000).toFixed(1)}M-$${(newCampaignData.priceMax/1000000).toFixed(1)}M)`,
            source_platform: 'redfin',
            status: 'active',
            total_properties_found: 0, // Will be updated after scraping
            created_by_admin_id: 'admin_user'
          })
          .select()
          .single();

        if (campaignError || !campaignData) {
          let errorMessage = 'Failed to create campaign';
          if (campaignError?.message?.includes('duplicate key value')) {
            errorMessage = 'Campaign name already exists. Please choose a different name.';
          } else if (campaignError) {
            errorMessage = `Database Error: ${campaignError.message || 'Unknown error occurred'}`;
          }
          throw new Error(errorMessage);
        }

        console.log('Redfin campaign created with UUID:', campaignData.id);
        
        // Store the campaign ID for later use
        setNewCampaignData(prev => ({ ...prev, campaignId: campaignData.id }));

        console.log('Step 2: Getting pending properties from Redfin with full details...');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-redfin-bulk-with-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            keyword: newCampaignData.redfinArea,
            areaType: newCampaignData.redfinAreaType, // Add area type
            campaignId: campaignData.id, // Use the real UUID
            priceMin: newCampaignData.priceMin || 500000,
            priceMax: newCampaignData.priceMax || 2000000,
            targetProperties: newCampaignData.targetProperties
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.details || errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.details || data.message || 'Failed to fetch Redfin properties with details');
        }

        setScrapedData(data.results);
        
        // Step 3: Update campaign with final property count
        console.log('Step 3: Updating Redfin campaign with final property count...');
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: data.results.successful || 0 })
          .eq('id', campaignData.id);

        if (updateError) {
          console.error('Error updating campaign property count:', updateError);
          // Don't fail the whole process for this
        }
        
        // Refresh campaigns list to show the new campaign
        await loadCampaigns();
        
        setScrapingStep('complete'); // Skip choose_pages for under contract
        
      } else if (isRedfinSoldCampaign) {
        console.log('Step 1: Creating Redfin SOLD campaign record first...');
        
        // Create campaign first to get a real UUID
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .insert({
            name: newCampaignData.name,
            campaign_type: 'sellers_agent_outreach',
            campaign_subtype: 'sold_sellers_agent',
            request_url: `${newCampaignData.redfinSoldArea} ($${(newCampaignData.priceMin/1000000).toFixed(1)}M-$${(newCampaignData.priceMax/1000000).toFixed(1)}M) - SOLD`,
            source_platform: 'redfin',
            status: 'active',
            total_properties_found: 0, // Will be updated after scraping
            created_by_admin_id: 'admin_user'
          })
          .select()
          .single();

        if (campaignError || !campaignData) {
          let errorMessage = 'Failed to create sold campaign';
          if (campaignError?.message?.includes('duplicate key value')) {
            errorMessage = 'Campaign name already exists. Please choose a different name.';
          } else if (campaignError) {
            errorMessage = `Database Error: ${campaignError.message || 'Unknown error occurred'}`;
          }
          throw new Error(errorMessage);
        }

        console.log('Redfin SOLD campaign created with UUID:', campaignData.id);
        
        // Store the campaign ID for later use
        setNewCampaignData(prev => ({ ...prev, campaignId: campaignData.id }));

        // Step 2: Start page-by-page scraping for sold properties
        console.log('Step 2: Starting page-by-page sold property scraping...');
        
        // Initialize progress tracking for sold properties
        setPageByPageScraping(true);
        setPageByPageCancelled(false);
        setTotalPropertiesFound(0);
        setTotalPropertiesWithEmails(0);
        setScrapingStep('page_by_page');

        // Store campaign data for progress tracking
        setNewCampaignData(prev => ({ 
          ...prev, 
          campaignId: campaignData.id,
          redfinArea: newCampaignData.redfinSoldArea, // Use sold area for progress tracking
          redfinAreaType: newCampaignData.redfinSoldAreaType
        }));

        // Start the page-by-page scraping process for sold properties
        await startPageByPageSoldScraping(campaignData.id);
        
      } else if (isHomesCampaign) {
        console.log('Step 1: Getting property list from homes.com...');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-homes-properties`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify({
            url: newCampaignData.homesUrl,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to scrape homes.com properties');
        }

        setScrapedData(data.data);
        
        // Step 2: Create campaign and store ALL properties immediately
        console.log('Step 2: Creating homes.com campaign and storing all properties...');
        await createCampaignAndStoreAllProperties(data.data);
        
        setScrapingStep('choose_pages');
      }
      
    } catch (error) {
      console.error('Error scraping properties:', error);
      setScrapingError(error instanceof Error ? error.message : 'An error occurred while scraping');
      setScrapingStep('url');
    } finally {
      setScrapingLoading(false);
    }
  };

  const createCampaignAndStoreAllProperties = async (scrapedData: any) => {
    try {
      // Create the campaign first
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          name: newCampaignData.name,
          campaign_type: 'buyers_agent_outreach',
          campaign_subtype: 'recently_closed_agent', // Default subtype, will be updated when user selects focus
          request_url: newCampaignData.homesUrl,
          source_platform: 'homes.com', // Add the required source_platform field
          notes: newCampaignData.notes,
          status: 'active',
          created_by_admin_id: 'admin_user'
        })
        .select()
        .single();

      if (campaignError || !campaignData) {
        let errorMessage = 'Failed to create campaign';
        if (campaignError?.message?.includes('duplicate key value')) {
          errorMessage = 'Campaign name already exists. Please choose a different name.';
        } else if (campaignError) {
          errorMessage = `Database Error: ${campaignError.message || 'Unknown error occurred'}`;
        }
        throw new Error(errorMessage);
      }

      console.log('Campaign created successfully:', campaignData);

      // Collect ALL properties from all pages
      const allProperties: any[] = [];
      
      if (scrapedData.allPages && Array.isArray(scrapedData.allPages)) {
        scrapedData.allPages.forEach((page: any) => {
          if (page.properties && Array.isArray(page.properties)) {
            page.properties.forEach((property: any) => {
              // Parse address from title
              const addressParts = property.title.split(', ');
              const streetAddress = addressParts[0] || '';
              const cityState = addressParts[1] || '';
              const zipCode = addressParts[2] || '';
              
              // Split city and state
              const cityStateParts = cityState.split(' ');
              const state = cityStateParts.pop() || '';
              const city = cityStateParts.join(' ') || '';

              allProperties.push({
                ...property,
                pageNumber: page.pageNumber,
                pageUrl: page.url,
                parsedAddress: {
                  streetAddress,
                  city,
                  state,
                  zipCode
                }
              });
            });
          }
        });
      }

      console.log(`Storing ${allProperties.length} properties to database...`);

      // Store all properties in database with basic info
      const propertyInserts = allProperties.map((property) => ({
        campaign_id: campaignData.id,
        campaign_type: 'buyers_agent_outreach',
        
        // Address information parsed from title
        address_line_1: property.parsedAddress.streetAddress,
        city: property.parsedAddress.city,
        state: property.parsedAddress.state,
        zip_code: property.parsedAddress.zipCode,
        
        // URLs
        homes_property_url: property.href,
        
        // Source and metadata
        source_platform: 'homes.com',
        raw_data: {
          originalProperty: property,
          scrapePage: property.pageNumber,
          pageUrl: property.pageUrl
        },
        processing_status: 'pending', // Status to indicate properties ready for detailed processing
        created_by_admin_id: 'admin_user'
      }));

      // Insert in batches to avoid overwhelming the database
      const batchSize = 50;
      for (let i = 0; i < propertyInserts.length; i += batchSize) {
        const batch = propertyInserts.slice(i, i + batchSize);
        const { error: propertyError } = await supabase
          .from('scraped_properties')
          .insert(batch);

        if (propertyError) {
          console.error('Error saving property batch:', propertyError);
          throw new Error(`Failed to save properties: ${propertyError.message}`);
        }
      }

      console.log('All properties stored successfully');
      
      // Store the campaign ID for later use
      setNewCampaignData(prev => ({ ...prev, campaignId: campaignData.id }));
      
      // Refresh campaigns list
      await loadCampaigns();
      
    } catch (error) {
      console.error('Error creating campaign and storing properties:', error);
      throw error;
    }
  };

  const handleScrapeTypeSelection = async (type: 'recently_sold' | 'sold_year_ago') => {
    setSelectedScrapeType(type);
    
    try {
      // Update campaign subtype based on user selection
      if (newCampaignData.campaignId) {
        const campaignSubtype = type === 'recently_sold' ? 'recently_closed_agent' : 'closing_anniversary_agent';
        
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ campaign_subtype: campaignSubtype })
          .eq('id', newCampaignData.campaignId);

        if (updateError) {
          console.error('Error updating campaign subtype:', updateError);
          // Don't throw error, just log it as this is not critical for the flow
        }
      }
      
      // Close the popup and navigate to the campaign properties table
      setShowNewCampaignPopup(false);
      
      // Navigate to the newly created campaign
      if (newCampaignData.campaignId) {
        const campaign = campaigns.find(c => c.id === newCampaignData.campaignId);
        if (campaign) {
          navigateToCampaign(campaign);
        }
      }
      
    } catch (error) {
      console.error('Error navigating to campaign:', error);
      setScrapingError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  // Function to process zip codes sequentially for county mode
  const scrapeZipCodeSequentially = async (campaignId: string, zipCodes: string[], zipIndex: number) => {
    console.log(`🎯 scrapeZipCodeSequentially called: zipIndex=${zipIndex}, totalZips=${zipCodes.length}`);
    
    if (pageByPageCancelled) {
      console.log('❌ Scraping cancelled, stopping zip code processing');
      setPageByPageScraping(false);
      setScrapingStep('complete');
      return;
    }
    
    if (zipIndex >= zipCodes.length) {
      console.log('✅ All zip codes completed!');
      setPageByPageScraping(false);
      setScrapingStep('complete');
      return;
    }

    // Check if target reached
    if (totalPropertiesWithEmails >= (newCampaignData.targetProperties || 50)) {
      console.log('🎯 Target reached! Stopping zip code processing');
      setPageByPageScraping(false);
      setScrapingStep('complete');
      return;
    }

    const currentZip = zipCodes[zipIndex];
    console.log(`📍 Starting zip code ${zipIndex + 1}/${zipCodes.length}: ${currentZip}`);

    // Update progress to show current zip code
    setPageByPageProgress(prev => ({
      ...prev,
      currentZipIndex: zipIndex,
      currentZipCode: currentZip,
      currentPage: 1
    }));

    // Reset page counter for this zip code
    setCurrentPage(1);

    // Start page-by-page scraping for this specific zip code
    await scrapeZipCodePages(campaignId, currentZip, zipIndex, 1, zipCodes);
  };

  // Function to scrape pages for a specific zip code
  const scrapeZipCodePages = async (campaignId: string, zipCode: string, zipIndex: number, pageNumber: number, allZipCodes: string[]) => {
    if (pageByPageCancelled) {
      console.log('Scraping cancelled, stopping');
      return;
    }

    // Check if target reached (different logic for Zillow vs others)
    const isZillowCampaign = newCampaignData.zillowArea.trim() !== '';
    const targetReached = isZillowCampaign 
      ? pageNumber >= (newCampaignData.targetPages || 3) // Page-based for Zillow
      : totalPropertiesWithEmails >= (newCampaignData.targetProperties || 50); // Email-based for others
    
    if (targetReached) {
      console.log(`🎯 Target reached! ${isZillowCampaign ? `Scraped ${pageNumber} pages` : `Found ${totalPropertiesWithEmails} properties with emails`}`);
      
      // Mark current zip as completed
      setPageByPageProgress(prev => ({
        ...prev,
        zipCodeProgress: prev.zipCodeProgress.map((zip, index) => 
          index === zipIndex ? { ...zip, completed: true } : zip
        )
      }));

      setPageByPageScraping(false);
      setScrapingStep('complete');
      return;
    }

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      console.log(`Scraping zip ${zipCode} page ${pageNumber}...`);
      
      setPageByPageProgress(prev => ({
        ...prev,
        currentPage: pageNumber
      }));

      const endpoint = newCampaignData.zillowArea.trim() !== '' 
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/skinny-zillow-bulk-scrape`
        : newCampaignData.redfinSoldArea?.trim() !== ''
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-redfin-sold-page`
        : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-redfin-page`;

      const requestBody = newCampaignData.zillowArea.trim() !== ''
        ? {
          keyword: newCampaignData.zillowArea,
          campaignId: campaignId,
          priceMin: newCampaignData.priceMin || 100000,
          priceMax: newCampaignData.priceMax || 10000000,
          pageNumber: pageNumber,
          listingType: 'sold',
          allowDuplicates: true
        } : newCampaignData.redfinSoldArea?.trim() !== ''
        ? {
          keyword: newCampaignData.redfinSoldArea,
          campaignId: campaignId,
          priceMin: newCampaignData.priceMin || 100000,
          priceMax: newCampaignData.priceMax || 10000000,
          pageNumber: pageNumber
        } : {
          keyword: zipCode, // Use the individual zip code parameter, not the original area
          campaignId: campaignId,
          priceMin: newCampaignData.priceMin || 500000,
          priceMax: newCampaignData.priceMax || 2000000,
          pageNumber: pageNumber
        };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-Admin-Token': adminToken,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.details || data.message || 'Failed to scrape page');
      }

      const results = data.results;
      console.log(`✅ Zip ${zipCode} Page ${pageNumber} results:`, results);
      
      // Update cumulative totals
      const newTotalFound = totalPropertiesFound + (results.total_found_on_page || 0);
      const newTotalWithEmails = totalPropertiesWithEmails + (results.successful_with_email || 0);
      
      setTotalPropertiesFound(newTotalFound);
      setTotalPropertiesWithEmails(newTotalWithEmails);

      // Update zip code specific progress
      setPageByPageProgress(prev => ({
        ...prev,
        propertiesFound: newTotalFound,
        propertiesWithEmails: newTotalWithEmails,
        zipCodeProgress: prev.zipCodeProgress.map((zip, index) => 
          index === zipIndex ? {
            ...zip,
            propertiesFound: zip.propertiesFound + (results.total_found_on_page || 0),
            propertiesWithEmails: zip.propertiesWithEmails + (results.successful_with_email || 0),
            pages: pageNumber
          } : zip
        ),
        lastPageResults: results
      }));

      // Check if we should continue with this zip code
      if (!data.has_more_pages || (results.total_found_on_page === 0)) {
        console.log(`📄 No more pages for zip ${zipCode}, moving to next zip`);
        
        // Mark current zip as completed
        setPageByPageProgress(prev => ({
          ...prev,
          zipCodeProgress: prev.zipCodeProgress.map((zip, index) => 
            index === zipIndex ? { ...zip, completed: true } : zip
          )
        }));

        // Move to next zip code using the passed array
        setTimeout(() => {
          if (!pageByPageCancelled) {
            console.log(`🚀 Moving to next zip code: ${zipIndex + 1}/${allZipCodes.length}`);
            scrapeZipCodeSequentially(campaignId, allZipCodes, zipIndex + 1);
          }
        }, 1000);
        return;
      }

      // Check if target reached after this page (different logic for Zillow vs others)
      const isZillowCampaign = newCampaignData.zillowArea.trim() !== '';
      const targetReached = isZillowCampaign 
        ? pageNumber >= (newCampaignData.targetPages || 3) // Page-based for Zillow
        : newTotalWithEmails >= (newCampaignData.targetProperties || 50); // Email-based for others
      
      if (targetReached) {
        console.log(`🎯 Target reached! ${isZillowCampaign ? `Scraped ${pageNumber} pages` : `Found ${newTotalWithEmails} properties with emails`}`);
        
        // Mark current zip as completed
        setPageByPageProgress(prev => ({
          ...prev,
          zipCodeProgress: prev.zipCodeProgress.map((zip, index) => 
            index === zipIndex ? { ...zip, completed: true } : zip
          )
        }));

        setPageByPageScraping(false);
        setScrapingStep('complete');
        
        // Update campaign with final count
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: newTotalWithEmails })
          .eq('id', campaignId);

        if (updateError) {
          console.error('Error updating campaign count:', updateError);
        }
        
        await loadCampaigns();
        return;
      }

      // Continue with next page for this zip code
      setTimeout(() => {
        if (!pageByPageCancelled) {
          scrapeZipCodePages(campaignId, zipCode, zipIndex, pageNumber + 1, allZipCodes);
        }
      }, 1000);

    } catch (error) {
      console.error(`❌ Error scraping zip ${zipCode} page ${pageNumber}:`, error);
      setScrapingError(error instanceof Error ? error.message : 'An error occurred during scraping');
      setPageByPageScraping(false);
    }
  };

  // Page-by-page scraping functions
  const startPageByPageScraping = async (existingCampaignId?: string) => {
    if (!newCampaignData.zillowArea.trim() && !newCampaignData.redfinArea.trim() && !newCampaignData.redfinSoldArea?.trim()) {
      setScrapingError('Please enter either Zillow area, Redfin area, or Redfin sold area for page-by-page scraping.');
      return;
    }
    
    if (!newCampaignData.name.trim()) {
      setScrapingError('Please enter a campaign name.');
      return;
    }

    try {
      setScrapingStep('page_by_page');
      setPageByPageScraping(true);
      setPageByPageCancelled(false);
      setCurrentPage(1);
      setTotalPropertiesFound(0);
      setTotalPropertiesWithEmails(0);
      setScrapingError(null);

      let campaignData: { id: string };
      
      // Define campaign type variables
      const isZillowCampaign = newCampaignData.zillowArea.trim() !== '';
      const isRedfinCampaign = newCampaignData.redfinArea.trim() !== '';
      
      // Check if campaign already exists (for sold campaigns) or use provided ID
      if (existingCampaignId || newCampaignData.campaignId) {
        const campaignId = existingCampaignId || newCampaignData.campaignId;
        console.log('Using existing campaign ID:', campaignId);
        campaignData = { id: campaignId };
      } else {
        // Create campaign first
        console.log('Creating campaign for page-by-page scraping...');
        
        const { data: newCampaignData_created, error: campaignError } = await supabase
          .from('campaigns')
          .insert({
            name: newCampaignData.name,
            campaign_type: 'sellers_agent_outreach',
            campaign_subtype: 'under_contract_sellers_agent',
            request_url: isZillowCampaign 
              ? `${newCampaignData.zillowArea} ($${(newCampaignData.priceMin/1000000).toFixed(1)}M-$${(newCampaignData.priceMax/1000000).toFixed(1)}M)`
              : `${newCampaignData.redfinArea} ($${(newCampaignData.priceMin/1000000).toFixed(1)}M-$${(newCampaignData.priceMax/1000000).toFixed(1)}M)`,
            source_platform: isZillowCampaign ? 'zillow' : 'redfin',
            status: 'active',
            total_properties_found: 0, // Will be updated as we scrape
            created_by_admin_id: 'admin_user'
          })
          .select()
          .single();

        if (campaignError || !newCampaignData_created) {
          let errorMessage = 'Failed to create campaign';
          if (campaignError?.message?.includes('duplicate key value')) {
            errorMessage = 'Campaign name already exists. Please choose a different name.';
          } else if (campaignError) {
            errorMessage = `Database Error: ${campaignError.message || 'Unknown error occurred'}`;
          }
          throw new Error(errorMessage);
        }

        campaignData = newCampaignData_created;
        console.log('Campaign created with UUID:', campaignData.id);
        
        // Store the campaign ID for later use
        setNewCampaignData(prev => ({ ...prev, campaignId: campaignData.id }));
      }

      // Handle county mode for Redfin
      if (isRedfinCampaign && newCampaignData.redfinAreaType === 'county') {
        console.log('🗺️ County mode: Converting county to zip codes...');
        
        setPageByPageProgress(prev => ({
          ...prev,
          isCountyMode: true,
          currentZipCode: 'Converting county...'
        }));

        try {
          const adminToken = adminAuth.getApiToken();
          if (!adminToken) {
            throw new Error('Admin authentication required');
          }
          
          const countyResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-county-zipcodes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({ county: newCampaignData.redfinArea })
          });

          if (!countyResponse.ok) {
            const errorData = await countyResponse.json().catch(() => ({}));
            throw new Error(`County conversion failed: ${errorData.error || countyResponse.statusText}`);
          }

          const countyData = await countyResponse.json();
          
          if (!countyData.success || !countyData.data || !countyData.data.zip_codes) {
            throw new Error('Failed to get zip codes for county');
          }

          const zipCodes = countyData.data.zip_codes;
          console.log(`✅ County "${countyData.data.county_name}, ${countyData.data.state}" converted to ${zipCodes.length} zip codes`);

          setPageByPageProgress(prev => ({
            ...prev,
            zipCodes: zipCodes,
            currentZipIndex: 0,
            currentZipCode: zipCodes[0] || '',
            zipCodeProgress: zipCodes.map((zip: string) => ({
              zipCode: zip,
              completed: false,
              propertiesFound: 0,
              propertiesWithEmails: 0,
              pages: 0
            }))
          }));

          // Start scraping the first zip code
          await scrapeZipCodeSequentially(campaignData.id, zipCodes, 0);

        } catch (error) {
          console.error('❌ Error converting county to zip codes:', error);
          setScrapingError(error instanceof Error ? error.message : 'Failed to convert county to zip codes');
          setPageByPageScraping(false);
          return;
        }
      } else {
        // Regular single zip code or Zillow mode
        setPageByPageProgress(prev => ({
          ...prev,
          isCountyMode: false,
          currentZipCode: isZillowCampaign ? newCampaignData.zillowArea : newCampaignData.redfinArea
        }));

        // Start scraping the single area
        await scrapeNextPage(campaignData.id, 1);
      }

    } catch (error) {
      console.error('Error starting page-by-page scraping:', error);
      setScrapingError(error instanceof Error ? error.message : 'An error occurred');
      setPageByPageScraping(false);
    }
  };

  const scrapeNextPage = async (campaignId: string, pageNumber: number) => {
    if (pageByPageCancelled) {
      console.log('Scraping cancelled, stopping');
      return;
    }

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      console.log(`Scraping page ${pageNumber}...`);
      
      setPageByPageProgress(prev => ({
        ...prev,
        currentPage: pageNumber,
        lastPageResults: null
      }));

      // Determine if this is a Zillow or Redfin campaign
      const isZillowCampaign = newCampaignData.zillowArea.trim() !== '';
      const isRedfinCampaign = newCampaignData.redfinArea.trim() !== '';
      const isRedfinSoldCampaign = newCampaignData.redfinSoldArea?.trim() !== '';

      const endpoint = isZillowCampaign 
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/skinny-zillow-bulk-scrape`
        : isRedfinSoldCampaign
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-redfin-sold-page`
        : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-redfin-page`;

      const requestBody = isZillowCampaign ? {
        keyword: newCampaignData.zillowArea,
        campaignId: campaignId,
        priceMin: newCampaignData.priceMin || 1000000,
        priceMax: newCampaignData.priceMax || 5000000,
        pageNumber: pageNumber,
        listingType: 'sold', // Can be made dynamic later: newCampaignData.zillowArea ? 'forSale' : 'sold'
        allowDuplicates: true
      } : isRedfinSoldCampaign ? {
        keyword: newCampaignData.redfinSoldArea,
        campaignId: campaignId,
        priceMin: newCampaignData.priceMin || 100000,
        priceMax: newCampaignData.priceMax || 10000000,
        pageNumber: pageNumber
      } : {
        keyword: newCampaignData.redfinArea,
        campaignId: campaignId,
        priceMin: newCampaignData.priceMin || 500000,
        priceMax: newCampaignData.priceMax || 2000000,
        pageNumber: pageNumber
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-Admin-Token': adminToken,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle timeout errors (408) or specific timeout indicators
        if (response.status === 408 || errorData.timeout || errorData.continue_next_page) {
          console.warn(`⏰ Page ${pageNumber} timed out, continuing to next page...`);
          
          // Log the timeout but continue to next page
          setPageByPageProgress(prev => ({
            ...prev,
            lastPageResults: {
              ...prev.lastPageResults,
              page_number: pageNumber,
              total_found_on_page: 0,
              processed: 0,
              successful_with_email: 0,
              failed: 1,
              errors: [`Page ${pageNumber} timed out - continuing to next page`],
              timeout: true
            }
          }));
          
          // Continue to next page after timeout
          setTimeout(() => {
            if (!pageByPageCancelled) {
              console.log(`⏭️ Continuing to page ${pageNumber + 1} after timeout...`);
              scrapeNextPage(campaignId, pageNumber + 1);
            }
          }, 2000); // Wait 2 seconds before continuing
          
          return; // Exit this function, don't throw error
        }
        
        throw new Error(errorData.details || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      console.log(`📊 Page ${pageNumber} response data:`, {
        success: data.success,
        timeout_partial: data.timeout_partial,
        partial_results: data.partial_results,
        has_more_pages: data.has_more_pages,
        message: data.message
      });
      
      // Handle partial success due to timeout
      if (data.success && data.timeout_partial) {
        console.warn(`⏰ Page ${pageNumber} completed with partial results due to timeout: ${data.results.successful} properties saved`);
        
        // Update progress with the successful results
        const results = data.results;
        setPageByPageProgress(prev => ({
          ...prev,
          lastPageResults: {
            page_number: pageNumber,
            total_found_on_page: results.total_found_on_page || 0,
            processed: results.processed || 0,
            successful_with_email: results.successful || 0,
            failed: results.failed || 0,
            errors: results.errors || [],
            timeout_partial: true
          }
        }));
        
        // Update cumulative totals
        const newTotalFound = totalPropertiesFound + (results.total_found_on_page || 0);
        const newTotalWithEmails = totalPropertiesWithEmails + (results.successful || 0); // Use successful instead of successful_with_email for now
        setTotalPropertiesFound(newTotalFound);
        setTotalPropertiesWithEmails(newTotalWithEmails);
        
        // Stop scraping here - don't continue to next page due to timeout
        console.log(`🛑 Stopping page-by-page scraping due to timeout with partial success`);
        setPageByPageProgress(prev => ({
          ...prev,
          status: 'Completed (partial due to timeout)',
          isActive: false
        }));
        
        return; // Don't continue to next page
      }
      
      if (!data.success) {
        throw new Error(data.details || data.message || 'Failed to scrape page');
      }

      const results = data.results;
      console.log(`✅ Page ${pageNumber} results:`, results);
      
      // Update cumulative totals
      const newTotalFound = totalPropertiesFound + (results.total_found_on_page || 0);
      const newTotalWithEmails = totalPropertiesWithEmails + (results.successful_with_email || 0);
      
      setTotalPropertiesFound(newTotalFound);
      setTotalPropertiesWithEmails(newTotalWithEmails);

      // Check if target reached
      const targetReached = newTotalWithEmails >= (newCampaignData.targetProperties || 50);
      
      // Update progress state
      setPageByPageProgress(prev => ({
        ...prev,
        currentPage: pageNumber,
        totalPages: pageNumber, // We don't know total pages until we hit the end
        propertiesFound: newTotalFound,
        propertiesWithEmails: newTotalWithEmails,
        targetReached: targetReached,
        lastPageResults: results
      }));

      console.log(`📊 After page ${pageNumber}: ${newTotalWithEmails}/${newCampaignData.targetProperties} with emails (target ${targetReached ? 'REACHED' : 'not reached'})`);

      // Check if we should continue
      if (targetReached) {
        console.log('🎯 Target reached! Stopping page-by-page scraping');
        setPageByPageScraping(false);
        setScrapingStep('complete');
        
        // Update campaign with final count
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: newTotalWithEmails })
          .eq('id', campaignId);

        if (updateError) {
          console.error('Error updating campaign count:', updateError);
        }
        
        await loadCampaigns();
        return;
      }

      // Check if no more pages (no properties found on this page or page limit reached)
      if (!data.has_more_pages || (results.total_found_on_page === 0) || data.page_limit_reached) {
        const reason = data.page_limit_reached ? 'Page limit reached (max ~20 pages)' : 'No more pages available';
        console.log(`📄 ${reason}, stopping`);
        setPageByPageScraping(false);
        setScrapingStep('complete');
        
        // Update campaign with final count
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: newTotalWithEmails })
          .eq('id', campaignId);

        if (updateError) {
          console.error('Error updating campaign count:', updateError);
        }
        
        await loadCampaigns();
        return;
      }

      // Safety check: Don't go beyond page 25 (Zillow typically limits to ~20)
      if (pageNumber >= 25) {
        console.log('📄 Reached safety limit of 25 pages, stopping');
        setPageByPageScraping(false);
        setScrapingStep('complete');
        
        // Update campaign with final count
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ total_properties_found: newTotalWithEmails })
          .eq('id', campaignId);

        if (updateError) {
          console.error('Error updating campaign count:', updateError);
        }
        
        await loadCampaigns();
        return;
      }

      // Schedule next page
      console.log(`⏭️ Scheduling page ${pageNumber + 1}...`);
      setTimeout(() => {
        if (!pageByPageCancelled) {
          scrapeNextPage(campaignId, pageNumber + 1);
        }
      }, 1000); // 1 second delay between pages

    } catch (error) {
      console.error(`❌ Error scraping page ${pageNumber}:`, error);
      setScrapingError(error instanceof Error ? error.message : 'An error occurred during scraping');
      setPageByPageScraping(false);
    }
  };

  const cancelPageByPageScraping = () => {
    console.log('Cancelling page-by-page scraping...');
    setPageByPageCancelled(true);
    setPageByPageScraping(false);
    
    if (newCampaignData.campaignId) {
      completeScraping(newCampaignData.campaignId, totalPropertiesWithEmails);
    } else {
      setScrapingStep('complete');
    }
  };

  const completeScraping = async (campaignId: string, finalCount: number) => {
    console.log('Completing scraping...');
    
    try {
      // Update campaign with final property count
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({ total_properties_found: finalCount })
        .eq('id', campaignId);

      if (updateError) {
        console.error('Error updating campaign property count:', updateError);
      }
      
      // Refresh campaigns list
      await loadCampaigns();
      
    } catch (error) {
      console.error('Error completing scraping:', error);
    } finally {
      setPageByPageScraping(false);
      setScrapingStep('complete');
    }
  };

  const navigateToCampaign = (campaign: Campaign) => {
    // Navigate to the properties table for all campaigns
    navigate(`/admin/campaigns/properties?campaign_id=${campaign.id}`);
  };

  // Delete campaign functions
  const handleDeleteCampaign = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      setDeletingCampaign(true);

      // Delete all related data first
      console.log('Deleting campaign and related data...');

      // Delete scraped properties for this campaign
      const { error: propertiesError } = await supabase
        .from('scraped_properties')
        .delete()
        .eq('campaign_id', campaignToDelete.id);

      if (propertiesError) {
        console.error('Error deleting properties:', propertiesError);
        // Continue anyway, as the campaign might not have properties
      }

      // Delete the campaign itself
      const { error: campaignError } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignToDelete.id);

      if (campaignError) {
        throw campaignError;
      }

      // Update the campaigns list
      setCampaigns(campaigns.filter(c => c.id !== campaignToDelete.id));
      
      // Remove from stats
      setCampaignStats(prev => {
        const newStats = { ...prev };
        delete newStats[campaignToDelete.id];
        return newStats;
      });

      // Close dialog
      setShowDeleteConfirmation(false);
      setCampaignToDelete(null);

      console.log('Campaign deleted successfully');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setDeletingCampaign(false);
    }
  };

  const cancelDeleteCampaign = () => {
    setShowDeleteConfirmation(false);
    setCampaignToDelete(null);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'active': 'bg-green-100 text-green-800',
      'paused': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-blue-100 text-blue-800',
      'archived': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCampaignTypeLabel = (type: string, subtype: string) => {
    const subtypeLabels: { [key: string]: string } = {
      'closing_anniversary_agent': 'Closing Anniversary',
      'recently_closed_agent': 'Recently Closed',
      'recently_closed_customer': 'Customer Outreach',
      'under_contract_sellers_agent': 'Recently Sold (Seller Agents)',
      'sold_sellers_agent': 'Sold (Seller Agents)'
    };
    return subtypeLabels[subtype] || type;
  };

  // Wrapper function for UI button clicks
  const handleStartPageByPageScraping = async () => {
    await startPageByPageScraping();
  };

  // Temporary sold scraping function - calls the sold-specific endpoint
  const startPageByPageSoldScraping = async (campaignId: string) => {
    console.log('🚀 Starting sold property scraping for campaign:', campaignId);
    
    // Don't override the state - the logic already correctly detects sold campaigns
    // based on newCampaignData.redfinSoldArea being set
    
    // Call the existing function and pass the campaign ID directly
    await startPageByPageScraping(campaignId);
  };

  // Don't render if not authenticated
  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaign Management</h1>
              <p className="text-gray-600">Create and manage your real estate outreach campaigns</p>
            </div>
            
            {/* Database Test Section */}
            <div className="flex flex-col items-end gap-3">
              <button
                onClick={testDatabase}
                disabled={testingDatabase}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {testingDatabase ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {testingDatabase ? 'Testing...' : 'Test Database'}
              </button>
              
              {/* Database Test Result */}
              {databaseTestResult && (
                <div className={`max-w-sm p-3 rounded-lg text-sm ${
                  databaseTestResult.includes('✅') 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  <div className="flex items-start gap-2">
                    <p className="flex-1">{databaseTestResult}</p>
                    <button
                      onClick={() => setDatabaseTestResult(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Fix Database Button for errors */}
                  {databaseTestResult.includes('❌') && (
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => window.open('/database-setup', '_blank')}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                      >
                        Fix Database
                      </button>
                      <button
                        onClick={testDatabase}
                        className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          
          {/* Add New Campaign Tile */}
          <div 
            onClick={handleNewCampaign}
            className="bg-white border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg p-8 text-center cursor-pointer transition-all hover:shadow-lg group"
          >
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-100 group-hover:bg-blue-200 rounded-full flex items-center justify-center mb-4 transition-colors">
                <Plus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">New Campaign</h3>
              <p className="text-sm text-gray-500">Create a new outreach campaign</p>
            </div>
          </div>

          {/* Existing Campaigns */}
          {campaigns.map((campaign) => {
            const stats = campaignStats[campaign.id];
            
            return (
              <div key={campaign.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                {/* Campaign Thumbnail */}
                {campaign.screenshot_thumbnail_url && (
                  <div className="w-full h-32 bg-gray-200 overflow-hidden">
                    <img
                      src={campaign.screenshot_thumbnail_url}
                      alt={`${campaign.name} screenshot`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 truncate pr-2">{campaign.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Home className="w-4 h-4 mr-2" />
                      {getCampaignTypeLabel(campaign.campaign_type, campaign.campaign_subtype)}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <MapPin className="w-3 h-3 mr-1" />
                      Source: {campaign.source_platform}
                    </div>
                  </div>

                  {/* Statistics */}
                  {stats ? (
                    <div className="space-y-3 mb-4">
                      {/* Total Properties */}
                      <div className="text-center bg-gray-50 rounded-lg p-2 border">
                        <div className="text-xl font-bold text-gray-900">{stats.total_properties}</div>
                        <div className="text-xs text-gray-500">Total Properties</div>
                      </div>
                      
                      {/* Funnel metrics - from not ready to purchased */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-gray-600">{stats.not_ready || 0}</div>
                          <div className="text-xs text-gray-500">Not Ready</div>
                        </div>
                        <div className="text-center bg-yellow-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-yellow-600">{stats.ready_to_send || 0}</div>
                          <div className="text-xs text-gray-500">Ready to Send</div>
                        </div>
                        <div className="text-center bg-blue-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-blue-600">{stats.email_sent || 0}</div>
                          <div className="text-xs text-gray-500">Email Sent</div>
                        </div>
                      </div>
                      
                      {/* Engagement & Conversion metrics */}
                      <div className="grid grid-cols-5 gap-1">
                        <div className="text-center bg-blue-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-blue-600">{stats.landing_page_accessed || 0}</div>
                          <div className="text-xs text-gray-500">Landing</div>
                        </div>
                        <div className="text-center bg-orange-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-orange-600">{stats.configurator_accessed || 0}</div>
                          <div className="text-xs text-gray-500">Configure</div>
                        </div>
                        <div className="text-center bg-yellow-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-yellow-600">{stats.order_summary_accessed || 0}</div>
                          <div className="text-xs text-gray-500">Order</div>
                        </div>
                        <div className="text-center bg-purple-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-purple-600">{stats.checkout_accessed || 0}</div>
                          <div className="text-xs text-gray-500">Checkout</div>
                        </div>
                        <div className="text-center bg-indigo-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-indigo-600">{stats.stripe_accessed || 0}</div>
                          <div className="text-xs text-gray-500">Stripe</div>
                        </div>
                      </div>
                      
                      {/* Purchase Outcome */}
                      <div className="grid grid-cols-1 gap-2">
                        <div className="text-center bg-green-50 rounded-lg p-3">
                          <div className="text-lg font-bold text-green-600">{stats.purchased || 0}</div>
                          <div className="text-xs text-gray-500">Purchased</div>
                        </div>
                      </div>
                      
                      {/* Revenue & Conversion rate if available */}
                      {stats.total_revenue > 0 && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center bg-emerald-50 rounded-lg p-2">
                            <div className="text-sm font-bold text-emerald-600">${(stats.total_revenue / 100).toFixed(0)}</div>
                            <div className="text-xs text-gray-500">Revenue</div>
                          </div>
                          {stats.overall_conversion_rate > 0 && (
                            <div className="text-center bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-2 border border-blue-200">
                              <div className="text-sm font-bold text-blue-700">{stats.overall_conversion_rate}%</div>
                              <div className="text-xs text-gray-600">Conversion</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 mb-4">
                      {/* Loading state - funnel format */}
                      <div className="text-center bg-gray-50 rounded-lg p-2">
                        <div className="text-xl font-bold text-gray-400">-</div>
                        <div className="text-xs text-gray-500">Total Properties</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Not Ready</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Ready to Send</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-lg font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Email Sent</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Opened</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Checkout</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Stripe</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-gray-400">-</div>
                          <div className="text-xs text-gray-500">Purchased</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigateToCampaign(campaign)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Properties
                    </button>
                    <button
                      onClick={() => handleDeleteCampaign(campaign)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      title="Delete Campaign"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Created: {new Date(campaign.created_at).toLocaleDateString()}
                    </div>
                    {campaign.last_scraped_at && (
                      <div className="flex items-center mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        Last scraped: {new Date(campaign.last_scraped_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State - only show if no campaigns and not loading */}
        {campaigns.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <Users className="mx-auto h-16 w-16" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No campaigns yet</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first outreach campaign</p>
            <button
              onClick={handleNewCampaign}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium text-lg"
            >
              Create Your First Campaign
            </button>
          </div>
        )}
      </div>

      {/* New Campaign Popup */}
      {showNewCampaignPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {scrapingStep === 'url' && 'Create New Campaign'}
                  {scrapingStep === 'scraping' && 'Scraping Properties...'}
                  {scrapingStep === 'choose_pages' && 'Choose Scraping Strategy'}
                  {scrapingStep === 'final_scraping' && 'Processing Properties...'}
                  {scrapingStep === 'page_by_page' && 'Page-by-Page Scraping'}
                  {scrapingStep === 'complete' && 'Campaign Created!'}
                </h2>
                <button 
                  onClick={handleClosePopup}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={scrapingLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Step 1: URL Input */}
              {scrapingStep === 'url' && (
                <div className="space-y-6">
                  {/* Campaign Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newCampaignData.name}
                      onChange={(e) => setNewCampaignData({ ...newCampaignData, name: e.target.value })}
                      placeholder="Enter campaign name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Choose Campaign Type</h4>
                    <p className="text-sm text-gray-600 mb-4">Select one of the options below to configure your campaign:</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {/* Homes.com Option */}
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        newCampaignData.homesUrl.trim() ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center mb-3">
                          <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                          <h5 className="font-semibold text-gray-900">Sold Properties (Homes.com)</h5>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Target buyer agents for recently sold or anniversary properties</p>
                        <input
                          type="url"
                          value={newCampaignData.homesUrl}
                          onChange={(e) => {
                            setNewCampaignData({ ...newCampaignData, homesUrl: e.target.value, zillowArea: '', redfinArea: '' });
                          }}
                          placeholder="https://www.homes.com/search/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                      </div>

                      {/* Zillow Option */}
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        newCampaignData.zillowArea.trim() ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center mb-3">
                          <Calendar className="w-5 h-5 text-purple-600 mr-2" />
                          <h5 className="font-semibold text-gray-900">Recently Sold (Zillow)</h5>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Target seller agents for recently sold properties</p>
                        <input
                          type="text"
                          value={newCampaignData.zillowArea}
                          onChange={(e) => {
                            setNewCampaignData({ ...newCampaignData, zillowArea: e.target.value, homesUrl: '', redfinArea: '' });
                          }}
                          placeholder="East Hampton, NY"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm mb-3"
                        />
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Min Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMin}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMin: parseInt(e.target.value) || 0 })}
                              placeholder="100000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Max Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMax}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMax: parseInt(e.target.value) || 0 })}
                              placeholder="10000000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Pages to Scrape</label>
                          <input
                            type="number"
                            value={newCampaignData.targetPages}
                            onChange={(e) => setNewCampaignData({ ...newCampaignData, targetPages: parseInt(e.target.value) || 1 })}
                            placeholder="3"
                            min="1"
                            max="10"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Enter city, state, price range, and pages (~40-50 properties per page)</p>
                      </div>


                      {/* Redfin Option */}
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        newCampaignData.redfinArea.trim() ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center mb-3">
                          <Home className="w-5 h-5 text-orange-600 mr-2" />
                          <h5 className="font-semibold text-gray-900">Pending Listings (Redfin)</h5>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Target seller agents for properties currently pending on Redfin</p>
                        
                        {/* Area Type Selector */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Search By</label>
                          <select
                            value={newCampaignData.redfinAreaType}
                            onChange={(e) => setNewCampaignData({ 
                              ...newCampaignData, 
                              redfinAreaType: e.target.value as 'zipcode' | 'county',
                              redfinArea: '' // Clear area when changing type
                            })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                          >
                            <option value="zipcode">Zip Code</option>
                            <option value="county">County</option>
                          </select>
                        </div>
                        
                        <input
                          type="text"
                          value={newCampaignData.redfinArea}
                          onChange={(e) => {
                            setNewCampaignData({ ...newCampaignData, redfinArea: e.target.value, homesUrl: '', zillowArea: '' });
                          }}
                          placeholder={newCampaignData.redfinAreaType === 'zipcode' ? '07009' : '428/CT/Middlesex-County'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm mb-3"
                        />
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Min Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMin}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMin: parseInt(e.target.value) || 0 })}
                              placeholder="500000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Max Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMax}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMax: parseInt(e.target.value) || 0 })}
                              placeholder="2000000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                            />
                          </div>
                        </div>
                        {/* Email Requirement Option */}
                        <div className="mb-3">
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={newCampaignData.requireAgentEmail}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, requireAgentEmail: e.target.checked })}
                              className="mr-2 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                            />
                            <span className="text-gray-700">Show only properties with agent emails in table</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1 ml-6">
                            {newCampaignData.requireAgentEmail 
                              ? 'Table will filter to show only properties with valid agent emails' 
                              : 'Table will show all properties (including those without emails)'}
                          </p>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          {newCampaignData.redfinAreaType === 'zipcode' 
                            ? 'Enter ZIP code (e.g., "07009") $500K-$2M'
                            : 'Enter county ID/state/name (e.g., "428/CT/Middlesex-County") $500K-$2M'
                          }
                        </p>
                      </div>

                      {/* Sold Listings (Redfin) Option */}
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        newCampaignData.redfinSoldArea.trim() ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center mb-3">
                          <Home className="w-5 h-5 text-green-600 mr-2" />
                          <h5 className="font-semibold text-gray-900">Sold Listings (Redfin)</h5>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Target seller agents for properties that have been sold on Redfin</p>
                        
                        {/* Area Type Selector */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Search By</label>
                          <select
                            value={newCampaignData.redfinSoldAreaType}
                            onChange={(e) => setNewCampaignData({ 
                              ...newCampaignData, 
                              redfinSoldAreaType: e.target.value as 'zipcode' | 'county',
                              redfinSoldArea: '' // Clear area when changing type
                            })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                          >
                            <option value="zipcode">Zip Code</option>
                            <option value="county">County</option>
                          </select>
                        </div>
                        
                        <input
                          type="text"
                          value={newCampaignData.redfinSoldArea}
                          onChange={(e) => {
                            setNewCampaignData({ ...newCampaignData, redfinSoldArea: e.target.value, homesUrl: '', zillowArea: '', redfinArea: '' });
                          }}
                          placeholder={newCampaignData.redfinSoldAreaType === 'zipcode' ? '07009' : '428/CT/Middlesex-County'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 text-sm mb-3"
                        />
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Min Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMin}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMin: parseInt(e.target.value) || 0 })}
                              placeholder="500000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Max Price</label>
                            <input
                              type="number"
                              value={newCampaignData.priceMax}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, priceMax: parseInt(e.target.value) || 0 })}
                              placeholder="2000000"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                          </div>
                        </div>
                        {/* Email Requirement Option */}
                        <div className="mb-3">
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={newCampaignData.requireAgentEmail}
                              onChange={(e) => setNewCampaignData({ ...newCampaignData, requireAgentEmail: e.target.checked })}
                              className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <span className="text-gray-700">Show only properties with seller agent emails in table</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1 ml-6">
                            {newCampaignData.requireAgentEmail 
                              ? 'Table will filter to show only sold properties with valid seller agent emails' 
                              : 'Table will show all sold properties (including those without emails)'}
                          </p>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          {newCampaignData.redfinSoldAreaType === 'zipcode' 
                            ? 'Enter ZIP code (e.g., "07009") $500K-$2M for sold properties'
                            : 'Enter county ID/state/name (e.g., "428/CT/Middlesex-County") $500K-$2M for sold properties'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Target Properties - Only show for non-Zillow campaigns */}
                  {(!newCampaignData.zillowArea.trim() && (newCampaignData.homesUrl.trim() || newCampaignData.redfinArea.trim() || newCampaignData.redfinSoldArea.trim())) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Properties (Optional)
                      </label>
                      <input
                        type="number"
                        value={newCampaignData.targetProperties}
                        onChange={(e) => setNewCampaignData({ ...newCampaignData, targetProperties: parseInt(e.target.value) || 0 })}
                        placeholder="e.g., 50"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">Enter the target number of properties with agent emails to collect. Default is 50.</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={newCampaignData.notes}
                      onChange={(e) => setNewCampaignData({ ...newCampaignData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {scrapingError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{scrapingError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleClosePopup}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    {newCampaignData.zillowArea.trim() ? (
                      <>
                        <button
                          onClick={handleInitialScrape}
                          disabled={!newCampaignData.name}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium"
                        >
                          Bulk Scrape (Fast)
                        </button>
                        <button
                          onClick={handleStartPageByPageScraping}
                          disabled={!newCampaignData.name}
                          className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium"
                        >
                          Page-by-Page (Safer)
                        </button>
                      </>
                    ) : newCampaignData.redfinArea.trim() ? (
                      <>
                        <button
                          onClick={handleInitialScrape}
                          disabled={!newCampaignData.name}
                          className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium"
                        >
                          {newCampaignData.redfinAreaType === 'county' ? 'Bulk Scrape (County → Zip Codes)' : 'Bulk Scrape (Fast)'}
                        </button>
                        <button
                          onClick={handleStartPageByPageScraping}
                          disabled={!newCampaignData.name}
                          className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium"
                        >
                          Page-by-Page {newCampaignData.redfinAreaType === 'county' ? '(County → Zips)' : '(Safer)'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleInitialScrape}
                        disabled={!newCampaignData.name}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium"
                      >
                        Start Campaign
                      </button>
                    )}
                  </div>
                  
                  {/* County Mode Note */}
                  {newCampaignData.redfinArea.trim() && newCampaignData.redfinAreaType === 'county' && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-700 text-sm">
                        <strong>County Mode:</strong> Will automatically convert your county to zip codes and scrape each one. 
                        <br />• <strong>Bulk Scrape:</strong> Processes all zip codes quickly in parallel
                        <br />• <strong>Page-by-Page:</strong> Processes each zip code sequentially with user control (recommended)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Scraping Progress */}
              {scrapingStep === 'scraping' && (
                <div className="text-center py-8">
                  <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {newCampaignData.zillowArea.trim() ? 'Scraping Under Contract Properties' : 'Scraping Complete Property History'}
                  </h3>
                  <p className="text-gray-600">
                    {newCampaignData.zillowArea.trim() 
                      ? 'Finding properties currently under contract from Zillow...' 
                      : 'Analyzing ALL pages to capture 12 months of sales data...'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">This may take a few moments as we gather comprehensive data</p>
                </div>
              )}

              {/* Page-by-Page Scraping Progress */}
              {scrapingStep === 'page_by_page' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                      {pageByPageScraping ? (
                        <Loader className="w-16 h-16 text-blue-600 animate-spin" />
                      ) : (
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {pageByPageScraping ? `Scraping Page ${pageByPageProgress.currentPage}...` : 
                       (pageByPageProgress.targetReached ? 'Target Reached! 🎯' : 'Scraping Complete!')}
                    </h3>
                    <p className="text-gray-600">
                      {pageByPageScraping 
                        ? 'Processing properties page by page to avoid timeouts' 
                        : (pageByPageProgress.targetReached 
                          ? (newCampaignData.zillowArea.trim() !== '' 
                            ? `Target of ${newCampaignData.targetPages} pages reached! Scraped ${pageByPageProgress.currentPage} pages and found ${pageByPageProgress.propertiesFound} total properties.`
                            : `Target of ${newCampaignData.targetProperties} properties reached! Found ${pageByPageProgress.propertiesWithEmails} properties with agent emails.`)
                          : (newCampaignData.zillowArea.trim() !== ''
                            ? `Completed scraping ${pageByPageProgress.currentPage} pages and found ${pageByPageProgress.propertiesFound} total properties. No more pages available or target reached.`
                            : `Completed scraping ${pageByPageProgress.propertiesWithEmails} properties with agent emails. No more pages available or target reached.`))}
                    </p>
                    
                    {/* Current Status */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      {pageByPageProgress.isCountyMode ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-blue-900">County Mode:</span>
                              <span className="ml-2 text-blue-700">
                                {newCampaignData.redfinArea}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">Total Zip Codes:</span>
                              <span className="ml-2 text-blue-700">{pageByPageProgress.zipCodes.length}</span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">Current Zip:</span>
                              <span className="ml-2 text-blue-700">
                                {pageByPageProgress.currentZipCode} ({pageByPageProgress.currentZipIndex + 1}/{pageByPageProgress.zipCodes.length})
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">Current Page:</span>
                              <span className="ml-2 text-blue-700">{pageByPageProgress.currentPage}</span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">Properties Found:</span>
                              <span className="ml-2 text-blue-700">{pageByPageProgress.propertiesFound}</span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">With Emails:</span>
                              <span className="ml-2 text-blue-700">{pageByPageProgress.propertiesWithEmails}</span>
                            </div>
                          </div>

                          {/* Zip Code Progress */}
                          {pageByPageProgress.zipCodeProgress.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium text-gray-900 mb-2">Zip Code Progress:</h4>
                              <div className="max-h-32 overflow-y-auto space-y-1">
                                {pageByPageProgress.zipCodeProgress.map((zipProgress, index) => (
                                  <div 
                                    key={zipProgress.zipCode} 
                                    className={`flex justify-between items-center text-xs p-2 rounded ${
                                      index === pageByPageProgress.currentZipIndex 
                                        ? 'bg-blue-100 border border-blue-300' 
                                        : zipProgress.completed 
                                          ? 'bg-green-100' 
                                          : 'bg-gray-50'
                                    }`}
                                  >
                                    <span className="font-medium">
                                      {zipProgress.zipCode} 
                                      {index === pageByPageProgress.currentZipIndex && ' (current)'}
                                      {zipProgress.completed && ' ✓'}
                                    </span>
                                    <span>
                                      {zipProgress.propertiesWithEmails} emails | {zipProgress.pages} pages
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-blue-900">Source:</span>
                            <span className="ml-2 text-blue-700">
                              {newCampaignData.zillowArea.trim() ? `Zillow (${newCampaignData.zillowArea})` : 
                               newCampaignData.redfinArea.trim() ? `Redfin (${newCampaignData.redfinArea})` : 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-900">Status:</span>
                            <span className="ml-2 text-blue-700">
                              {newCampaignData.zillowArea.trim() ? 'Recently Sold (SOLD)' : 
                               newCampaignData.redfinArea.trim() ? 'Pending (under-contract)' : 'Processing'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-900">Current Page:</span>
                            <span className="ml-2 text-blue-700">{pageByPageProgress.currentPage}</span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-900">Properties Found:</span>
                            <span className="ml-2 text-blue-700">{pageByPageProgress.propertiesFound}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress Stats */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          {pageByPageProgress.currentPage || 0}
                        </div>
                        <div className="text-sm text-gray-600">Current Page</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">
                          {pageByPageProgress.propertiesWithEmails || 0}
                        </div>
                        <div className="text-sm text-gray-600">With Emails</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">
                          {pageByPageProgress.propertiesFound || 0}
                        </div>
                        <div className="text-sm text-gray-600">Total Found</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-orange-600">
                          {newCampaignData.zillowArea.trim() ? newCampaignData.targetPages : newCampaignData.targetProperties}
                        </div>
                        <div className="text-sm text-gray-600">
                          {newCampaignData.zillowArea.trim() ? 'Target Pages' : 'Target'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Progress to Target</span>
                      <span className={pageByPageProgress.targetReached ? "text-green-600 font-semibold" : ""}>
                        {newCampaignData.zillowArea.trim() ? 
                          `${pageByPageProgress.currentPage} / ${newCampaignData.targetPages}` :
                          `${pageByPageProgress.propertiesWithEmails} / ${newCampaignData.targetProperties}`
                        }
                        {pageByPageProgress.targetReached && " ✅"}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          pageByPageProgress.targetReached ? 'bg-green-600' : 'bg-blue-600'
                        }`}
                        style={{ 
                          width: `${Math.min(100, newCampaignData.zillowArea.trim() ? 
                            (pageByPageProgress.currentPage / newCampaignData.targetPages) * 100 :
                            (pageByPageProgress.propertiesWithEmails / newCampaignData.targetProperties) * 100
                          )}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Last Page Results */}
                  {pageByPageProgress.lastPageResults && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Last Page Results:</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>• Found {pageByPageProgress.lastPageResults.total_found_on_page} properties</div>
                        <div>• {pageByPageProgress.lastPageResults.price_filtered} matched price range</div>
                        <div>• {pageByPageProgress.lastPageResults.successful_with_email} had PENDING status with emails</div>
                        <div>• {pageByPageProgress.lastPageResults.skipped_not_pending} skipped (not PENDING)</div>
                        <div>• {pageByPageProgress.lastPageResults.skipped_no_email} skipped (no email)</div>
                      </div>
                    </div>
                  )}

                  {scrapingError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{scrapingError}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {pageByPageScraping ? (
                    <div className="flex justify-center">
                      <button
                        onClick={cancelPageByPageScraping}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium"
                      >
                        Stop Scraping
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => {
                          if (newCampaignData.campaignId) {
                            navigate(`/admin/campaigns/properties?campaign_id=${newCampaignData.campaignId}`);
                          }
                          handleClosePopup();
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
                      >
                        View Results ({pageByPageProgress.propertiesWithEmails} properties)
                      </button>
                      <button
                        onClick={handleClosePopup}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Choose Pages */}
              {scrapingStep === 'choose_pages' && scrapedData && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Properties Loaded Successfully!
                    </h3>
                    <p className="text-gray-600">
                      All {scrapedData.summary?.totalPropertiesFound || 0} properties have been stored. 
                      Choose your campaign focus to view and select properties for detailed scraping:
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleScrapeTypeSelection('recently_sold')}
                      className="p-6 border-2 border-gray-200 hover:border-blue-400 rounded-lg text-left transition-colors group"
                    >
                      <div className="flex items-center mb-3">
                        <TrendingUp className="w-6 h-6 text-blue-600 mr-3" />
                        <h4 className="text-lg font-semibold text-gray-900">Recently Sold</h4>
                      </div>
                      <p className="text-gray-600 mb-3">Focus on properties sold in the last 1-2 months</p>
                      <div className="text-sm text-blue-600 group-hover:text-blue-700 font-medium">
                        → View properties table and select for detailed scraping
                      </div>
                    </button>

                    <button
                      onClick={() => handleScrapeTypeSelection('sold_year_ago')}
                      className="p-6 border-2 border-gray-200 hover:border-green-400 rounded-lg text-left transition-colors group"
                    >
                      <div className="flex items-center mb-3">
                        <Calendar className="w-6 h-6 text-green-600 mr-3" />
                        <h4 className="text-lg font-semibold text-gray-900">Closing Anniversary</h4>
                      </div>
                      <p className="text-gray-600 mb-3">Focus on properties with 1-year closing anniversary</p>
                      <div className="text-sm text-green-600 group-hover:text-green-700 font-medium">
                        → View properties table and select for detailed scraping
                      </div>
                    </button>
                  </div>

                  {scrapingError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex flex-col gap-3">
                        <p className="text-red-700 text-sm">{scrapingError}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setScrapingError(null);
                              setScrapingStep('choose_pages');
                            }}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                          >
                            Retry Selection
                          </button>
                          <button
                            onClick={() => {
                              setScrapingError(null);
                              setScrapingStep('url');
                              setScrapedData(null);
                            }}
                            className="px-3 py-1 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                          >
                            Start Over
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Final Scraping Progress */}
              {scrapingStep === 'final_scraping' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Processing {selectedScrapeType === 'recently_sold' ? 'Recently Sold' : 'Anniversary'} Properties
                    </h3>
                    <p className="text-gray-600">Getting detailed information for each property...</p>
                  </div>

                  {scrapingProgress.total > 0 && (
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span>{scrapingProgress.current} of {scrapingProgress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(scrapingProgress.current / scrapingProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {scrapingError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex flex-col gap-3">
                        <p className="text-red-700 text-sm">{scrapingError}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setScrapingError(null);
                              setScrapingStep('final_scraping');
                            }}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                          >
                            Retry Processing
                          </button>
                          <button
                            onClick={() => {
                              setScrapingError(null);
                              setScrapingStep('choose_pages');
                              setScrapedData(null);
                            }}
                            className="px-3 py-1 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                          >
                            Start Over
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Complete */}
              {scrapingStep === 'complete' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Created Successfully!</h3>
                  <p className="text-gray-600 mb-6">
                    {newCampaignData.zillowArea.trim() 
                      ? (pageByPageProgress.propertiesFound > 0 
                          ? `Successfully processed ${pageByPageProgress.propertiesFound} recently sold properties from ${pageByPageProgress.currentPage} pages in "${newCampaignData.zillowArea}". Page-by-page scraping completed ${pageByPageCancelled ? 'via user cancellation' : 'successfully'}.`
                          : `Successfully processed ${scrapedData?.total_properties_found || 0} recently sold properties found in "${newCampaignData.zillowArea}". ${scrapedData?.timeout_reached ? 'Process completed due to timeout - you can run another scraping session to continue.' : 'Bulk scraping completed successfully.'}`)
                      : newCampaignData.redfinArea.trim()
                        ? (pageByPageProgress.propertiesWithEmails > 0 
                            ? `Successfully processed ${pageByPageProgress.propertiesWithEmails} under-contract properties with agent emails from ${pageByPageProgress.propertiesFound} total properties found in "${newCampaignData.redfinArea}" (${pageByPageProgress.currentPage} pages scraped). Page-by-page scraping completed ${pageByPageCancelled ? 'via user cancellation' : 'successfully'}.`
                            : `Successfully processed ${scrapedData?.successful || 0} properties with agent emails from ${scrapedData?.total_properties_found || 0} total properties found in "${newCampaignData.redfinArea}". ${scrapedData?.timeout_reached ? 'Process completed due to timeout - you can run another scraping session to continue.' : 'Bulk scraping completed successfully.'}`)
                        : `Created ${finalProperties.length} entries from the uploaded properties list.`}
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        if (newCampaignData.campaignId) {
                          navigate(`/admin/campaigns/properties?campaign_id=${newCampaignData.campaignId}`);
                        }
                        handleClosePopup();
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
                    >
                      View Campaign Properties
                    </button>
                    <button
                      onClick={handleClosePopup}
                      className="block w-full text-gray-600 hover:text-gray-800 text-sm"
                    >
                      Return to Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && campaignToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Campaign</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{campaignToDelete.name}</strong>"? 
              This will permanently remove the campaign and all its associated properties. 
              This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteCampaign}
                disabled={deletingCampaign}
                className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCampaign}
                disabled={deletingCampaign}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                {deletingCampaign ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDashboard; 